import { FileUploadOutlined } from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  useGenerateUploadLinkMutation,
  useUploadToAzureMutation,
} from "../../../redux/Documents/documentSlice";
import {
  CandidateDetails,
  useUpdateCandidateDetailsMutation,
} from "../../../redux/Hire/jobRequestApi";
import { userAccess } from "../../../utils/CommonFunction";

type Props = {
  candidate?: CandidateDetails;
};

const UpdateResume = ({ candidate }: Props) => {
  const [updateCandidate, { isLoading: isCandidateUpdating }] =
    useUpdateCandidateDetailsMutation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [getLink, { data, isSuccess, isLoading }] =
    useGenerateUploadLinkMutation();
  const [files, setFiles] = useState<File | null>(null);
  const [
    uploadFile,
    { isLoading: uploadIsLoading, isSuccess: isUploadSuccess, reset },
  ] = useUploadToAzureMutation();

  const clearInput = useCallback(() => {
    setFiles(null);
    if (inputRef && inputRef.current && inputRef.current.value)
      inputRef.current.value = "";
  }, []);

  const generateLink = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        const thisFile = e.target.files[0];
        const sanitizedFileName = thisFile.name
          .replace(/ /g, "_") // replace spaces with underscores
          .replace(/[^a-zA-Z0-9._-]/g, ""); // remove special characters except . _ -
        const modifiedFile = new File([thisFile], sanitizedFileName);
        const fileToShow = new File([thisFile], thisFile.name); //Original file name will be shown in the input
        getLink({
          fileName: modifiedFile.name,
          id: "",
        });
        setFiles(fileToShow);
      }
    },
    [getLink],
  );

  const uploadToAzure = useCallback(
    (link: string) => {
      if (files) {
        uploadFile({
          link,
          file: files,
          fileName: "Resume",
          id: "",
        });
      }
    },
    [files, uploadFile],
  );

  useEffect(() => {
    if (isSuccess && data) {
      uploadToAzure(data.link);
    }
  }, [isSuccess, data, uploadToAzure]);

  const { firstName, lastName, userId } = userAccess();
  const handleAzureUploadSuccess = useCallback(async () => {
    if (isUploadSuccess && data) {
      const linkSplit = data.link.split("/") || [];
      const fileName = linkSplit[linkSplit.length - 1]?.split("?")[0];
      await updateCandidate({
        ...candidate,
        updatedAt: new Date().toISOString(),
        email: candidate?.email || "",
        fileName: fileName,
        id: candidate?.id || 0,
        createdByName: `${firstName} ${lastName}`,
        createdByUId: userId,
      }).unwrap();
      clearInput();
      reset();
    }
  }, [
    candidate,
    clearInput,
    data,
    firstName,
    isUploadSuccess,
    lastName,
    reset,
    updateCandidate,
    userId,
  ]);

  useEffect(() => {
    handleAzureUploadSuccess();
  }, [handleAzureUploadSuccess]);

  return (
    <Button
      variant="text"
      disabled={isLoading || uploadIsLoading || isCandidateUpdating}
      component="label"
      endIcon={
        isLoading || uploadIsLoading || isCandidateUpdating ? (
          <CircularProgress size={24} />
        ) : (
          <FileUploadOutlined color="primary" />
        )
      }
    >
      Upload New Resume
      <input
        accept=".docx,.doc,.txt,application/msword,text/plain, application/pdf"
        style={{ display: "none" }}
        type="file"
        ref={inputRef}
        hidden
        onChange={generateLink}
      />
    </Button>
  );
};

export default UpdateResume;
